import * as Mui from "@mui/material";
import { useContext, useMemo } from "react";

import WarningIcon from "@mui/icons-material/Warning";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { ContextOperation } from "../../context/OperationContext";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import OperationTypes from "../../types";
import AutoComplete, { AutoCompleteOption } from "../common/AutoComplete";
import CheckInOutWrapper from "../common/CheckInOutWrapper";
import FormInput from "../common/FormInput";
import { nameof } from "../common/ObjectUtils";
import { EnumStatusReasons } from "../vehicle-availability/service/VehicleAvailabilityService";

export interface FormInputs {
  logsticLocation: string | null;
  position: string | null;
  batteryLevel: string | null;
  fuelTank: string | null;
  km: string | null;
  carTransporterExternalDeliveryNoteNumber: string | null;
  carTransporterUnloadingDate: string | null;
}

const VehicleEntrancePlate = (props: any) => {
  const navigate = useNavigate();
  const location = useLocation();
  const t = useOperationTranslation();

  props.changeSubtitle(t("vehicleEntrance.subtitles.firstCheckIn"));

  const { globalData } = useContext(GlobalContext);

  const { userLocations, tenant } = useContext(ContextOperation);

  const [warning, setWarning] = useState(false);

  const [disableButton, setDisableButton] = useState(true);

  const form = useForm<FormInputs>({
    defaultValues: {
      position: null,
      batteryLevel: null,
      fuelTank: null,
    },
  });
  const [chipLabel, setChipLabel] = useState<any>(null);
  const reg = /^[0-9\b]+$/;
  const vehicleData: OperationTypes.Vehicle = location.state as OperationTypes.Vehicle;

  useEffect(() => {
    if (vehicleData === null) {
      navigate(`/operation/transfer/vehiclesEntrance`);
    } else {
      setChipLabel(`${vehicleData.vehicleStatus}`);
    }

    
  }, [vehicleData]);

  const onSubmit = () => {
    navigate(`/operation/transfer/vehiclesEntrance/plate/checkList`, {
      state: {
        ...vehicleData,
        entranceLocationId: form.watch("logsticLocation") || null,
        carTransporterExternalDeliveryNoteNumber: form.watch("carTransporterExternalDeliveryNoteNumber") || null,
        carTransporterUnloadingDate: form.watch("carTransporterUnloadingDate") || null,
        // km: Number(form.watch("km")),
        // fuelLevel: Number(form.watch("fuelTank")) || null,
        // batteryLevel: Number(form.watch("batteryLevel")) || null,
      },
    });
  };

  const batteryValueInputLength = (value: string | null) => {
    const length = (value || "").length;

    if (length === 1) {
      return 10;
    }

    if (length === 2) {
      return 20;
    }

    if (length === 3) {
      return 29;
    }

    return 60;
  };

  const kmValueInputLength = (value: string | null) => {
    const length = (value || "").length;

    return length * 10;
  };

  const showElettricBatteryInput =
    vehicleData.enginePowerCode === "E" ||
    vehicleData.enginePowerCode === "HB" ||
    vehicleData.enginePowerCode === "HD";

  const showTermicLevelInput =
    vehicleData.enginePowerCode === "D" ||
    vehicleData.enginePowerCode === "G" ||
    vehicleData.enginePowerCode === "B" ||
    vehicleData.enginePowerCode === "HB" ||
    vehicleData.enginePowerCode === "HD";

  const [showCheckInOut, setShowCheckInOut] = useState(false);
  
  const locations = useMemo<AutoCompleteOption[]>(() => {
    return userLocations.map((loc) => ({
      label: loc.descr || "",
      value: loc.key || "",
    })) || [];
  }, [userLocations]);

  const onSumbitForm = (data: FormInputs) => {
    setShowCheckInOut(true)
  }

  return (
    vehicleData && (
      <>
        <Mui.Box paddingBottom="16px">
          <Mui.Grid container spacing={{ md: 2 }} p={{ md: 2 }}>
            <Mui.Grid item md={12} xs={12}>
              {!showCheckInOut &&(
              <Mui.Card>
                <Mui.CardContent>
                  <Mui.Stack gap={2} alignItems="center" mb={2}>
                    <Mui.Skeleton
                      variant="rectangular"
                      width="150px"
                      height="150px"
                    />
                    <Mui.Typography
                      variant="h4"
                      mb="10px"
                      textAlign={{ md: "left", xs: "center" }}
                    >
                      {`${vehicleData.model.brand.descr} ${vehicleData.model.descr}`}
                    </Mui.Typography>
                    <Mui.Chip
                      label={chipLabel}
                      color={
                        vehicleData.vehicleStatus ===
                        EnumStatusReasons.AVAILABLE
                          ? "info"
                          : "warning"
                      }
                    />
                  </Mui.Stack>

                  <Mui.Box sx={{ display: "flex", gap: "10px" }} pt="20px">
                    <Mui.Typography variant="body1">
                      {t("vehicleEntrance.status.plate")}:
                    </Mui.Typography>
                    <Mui.Typography variant="h5">
                      {vehicleData.plate}
                    </Mui.Typography>
                  </Mui.Box>

                <form onSubmit={form.handleSubmit(onSumbitForm)}>      
                  <Mui.Box pt="20px">
                    <AutoComplete
                      fullWidth
                      options={locations}
                      value={form.watch("logsticLocation") || ""}
                      onChange={(newStatusValue) => {
                        const newValueBpCode = userLocations.filter(
                          ({ key }: any) => key === newStatusValue
                        )[0]?.bpCode;

                        if (
                          newStatusValue &&
                          vehicleData?.operativeCenterEstimatedDeliveryId &&
                          newValueBpCode !==
                            vehicleData.operativeCenterEstimatedDeliveryId
                        ) {
                          setWarning(true);
                        }

                        if (newStatusValue !== "" && newStatusValue !== undefined && newStatusValue !== null) {
                          setDisableButton(false);
                        } else {
                          setDisableButton(true);
                        }

                        form.setValue(
                          "logsticLocation",
                          newStatusValue as string
                        );
                      }}
                      renderInput={(params) => (
                        <Mui.TextField
                          {...params}
                          variant="standard"
                          size="small"
                          {...form.register("logsticLocation", {
                            required: t(
                              "vehicleEntrance.status.lmsErrorMessage"
                            ),
                          })}
                          label={t("vehicleEntrance.entranceLogisticLabel")}
                          error={Boolean(
                            form.formState.errors?.logsticLocation?.message?.toString()
                          )}
                          helperText={
                            Boolean(
                              form.formState.errors?.logsticLocation?.message?.toString()
                            )
                              ? t("vehicleExit.status.driverError")
                              : ""
                          }
                          placeholder={t(
                            "vehicleEntrance.entranceLogisticLabel"
                          )}
                        />
                      )}
                    />
                  </Mui.Box>
                
                  <Mui.Box pt="20px">
                    <FormInput 
                      name={nameof<FormInputs>("carTransporterExternalDeliveryNoteNumber")}
                      type="text"
                      label={t("vehicleEntrance.externalBillNumberLabel")}
                      fullWidth
                      formInstance={form}
                      validation={{
                        required: t("vehicleEntrance.status.externalDDTError"),
                      }}
                    />          
                  </Mui.Box>

                  <Mui.Box pt="20px">
                    <FormInput 
                      name={nameof<FormInputs>("carTransporterUnloadingDate")}
                      type="date"
                      label={t("vehicleEntrance.scaricoDellaBisarcaLabel")}
                      fullWidth
                      formInstance={form}
                      inputFormat={"DD/MM/YYYY"}
                      removeTime={true}
                      maxDate={new Date()}
                      validation={{
                        required: t("vehicleEntrance.status.dateBatteryError"),
                      }}                  
                    />          
                  </Mui.Box>

                  <Mui.Grid item xs={12} mt={2}>
                    <Mui.Button
                      fullWidth
                      variant="contained"
                      disabled={disableButton}
                      type="submit"
                    >
                      {t("vehicleEntrance.confirm.checkInButton")}
                    </Mui.Button>
                  </Mui.Grid>
                  </form>
                
                </Mui.CardContent>
              </Mui.Card>
              )}

              {showCheckInOut && (
                  <CheckInOutWrapper
                    plate={vehicleData?.plate}
                    modelDescription={vehicleData?.model?.descr}
                    brandDescription={vehicleData?.model?.brand?.descr}
                    enginePowerGroupCode={vehicleData?.enginePowerCode}
                    currentFuelLevelEighths={vehicleData?.currentTermicTankFilling}
                    currentBatteryPercentage={vehicleData?.currentElectricTankFilling}
                    currentKms={vehicleData?.kms}
                    oprCallback={onSubmit}
                    callbackButtonLabel={t("vehicleEntrance.confirm.checkInCBButton")}
                    carClass={vehicleData.carClass.descr}
                    bypassDamagesEquipments={true}
					enableCounterPart={true}
					viewType='ddt'
                  />
              )}
              
            </Mui.Grid>
            {/* <Mui.Grid item md={6} xs={12}>
              <Mui.Card>
                <Mui.CardContent>
                  <form onSubmit={form.handleSubmit(onSubmit)}>
                    <Mui.Grid container spacing={0}>
                      <FormInput
                        name="km"
                        label={t("vehicleEntrance.status.kmLabel")}
                        formInstance={form}
                        type="text"
                        onChange={(event) => {
                          if (reg.test(event) || event === "") {
                            form.setValue("km", event);
                          }
                        }}
                        fullWidth
                        error={Boolean(form.formState.errors["km"])}
                        helperText={form.formState.errors[
                          "km"
                        ]?.message?.toString()}
                        sx={{
                          input: {
                            width: kmValueInputLength(form.getValues("km")),
                            marginBottom: 0,
                            paddingBottom: 0,
                          },
                        }}
                        InputProps={{
                          style: { paddingBottom: 10 },
                          endAdornment: (
                            <Mui.InputAdornment position="start">
                              {form.getValues("km") && "KM"}
                            </Mui.InputAdornment>
                          ),
                        }}
                      />

                      {showElettricBatteryInput && (
                        <>
                          <Mui.TextField
                            variant="standard"
                            label={t("vehicleEntrance.status.batteryLabel")}
                            placeholder={t("vehicleEntrance.search.exBattery")}
                            fullWidth
                            {...form.register("batteryLevel", {
                              required: t("vehicleEntrance.status.driverError"),
                            })}
                            value={form.watch("batteryLevel")}
                            onChange={(event) => {
                              if (
                                reg.test(event.target.value) ||
                                event.target.value === ""
                              ) {
                                if (event.target.value.length > 3) {
                                  return form.setValue(
                                    "batteryLevel",
                                    form.watch("batteryLevel")
                                  );
                                }
                                form.setValue(
                                  "batteryLevel",
                                  event.target.value
                                );
                              } else {
                                form.setValue("batteryLevel", "");
                              }
                            }}
                            helperText={
                              Number(form.getValues("batteryLevel")) > 100
                                ? t("vehicleEntrance.search.batteryMaxValue")
                                : form.formState.errors[
                                    "batteryLevel"
                                  ]?.message?.toString()
                            }
                            error={Boolean(
                              Number(form.getValues("batteryLevel")) > 100
                                ? true
                                : form.formState.errors["batteryLevel"]
                            )}
                            style={{ marginTop: 20 }}
                            sx={{
                              input: {
                                width: batteryValueInputLength(
                                  form.getValues("batteryLevel")
                                ),
                                marginBottom: 0,
                                paddingBottom: 0,
                              },
                            }}
                            InputProps={{
                              style: { paddingBottom: 10 },
                              endAdornment: (
                                <Mui.InputAdornment position="start">
                                  {form.getValues("batteryLevel") && "%"}
                                </Mui.InputAdornment>
                              ),
                            }}
                          />
                        </>
                      )}

                      {showTermicLevelInput && (
                        <>
                          <Mui.TextField
                            variant="standard"
                            label={t("vehicleEntrance.status.fuelLabel")}
                            placeholder={t("vehicleEntrance.search.exFuel")}
                            fullWidth
                            {...form.register("fuelTank", {
                              required: t("vehicleEntrance.status.driverError"),
                            })}
                            value={form.watch("fuelTank")}
                            onChange={(event) => {
                              if (
                                reg.test(event.target.value) ||
                                event.target.value === ""
                              ) {
                                if (event.target.value.length > 1) {
                                  return form.setValue(
                                    "fuelTank",
                                    form.watch("fuelTank")
                                  );
                                }
                                form.setValue("fuelTank", event.target.value);
                              } else {
                                form.setValue("fuelTank", "");
                              }
                            }}
                            helperText={
                              Number(form.watch("fuelTank")) > 8
                                ? t("vehicleEntrance.search.fuelMaxValue")
                                : form.formState.errors[
                                    "fuelTank"
                                  ]?.message?.toString()
                            }
                            error={Boolean(
                              Number(form.watch("fuelTank")) > 8
                                ? true
                                : form.formState.errors["fuelTank"]
                            )}
                            sx={{
                              input: {
                                width: batteryValueInputLength(
                                  form.getValues("fuelTank")
                                ),
                                marginBottom: 0,
                                paddingBottom: 0,
                              },
                            }}
                            style={{ marginTop: 20 }}
                            InputProps={{
                              style: { paddingBottom: 10 },
                              endAdornment: (
                                <Mui.InputAdornment position="start">
                                  {form.watch("fuelTank") && "/8"}
                                </Mui.InputAdornment>
                              ),
                            }}
                          />
                        </>
                      )}

                      <Mui.Grid item xs={12} mt={2}>
                        <Mui.Button
                          fullWidth
                          variant="contained"
                          style={{ color: "white", backgroundColor: "#d12c93" }}
                          type="submit"
                        >
                          {t("vehicleEntrance.confirm.continue")}
                        </Mui.Button>
                      </Mui.Grid>
                    </Mui.Grid>
                  </form>
                </Mui.CardContent>
              </Mui.Card>
            </Mui.Grid> */}
          </Mui.Grid>
          
          <Mui.Dialog open={warning}>
            <Mui.DialogTitle>
              <WarningIcon sx={{ color: "orange", fontSize: "20px" }} />
            </Mui.DialogTitle>

            <Mui.DialogContent>
              {t("vehicleEntrance.withPlate.confirm.diffrentFromRealLocation")}
            </Mui.DialogContent>

            <Mui.DialogActions>
              <Mui.Button variant="contained" onClick={() => setWarning(false)}>
                OK
              </Mui.Button>
            </Mui.DialogActions>
          </Mui.Dialog>
        </Mui.Box>
      </>
    )
  );
};
export default VehicleEntrancePlate;
